/**
 * Enregistrements DNS de vérification de domaine, présentés champ par champ.
 *
 * Ces valeurs ne sont pas là pour être lues : elles sont à recopier dans le formulaire d'un
 * registrar, qui demande séparément un type, un nom d'hôte et une valeur. Une ligne dense
 * « TXT · _exiplan.exemple.com · exiplan-verify=abc » oblige le lecteur à deviner quel fragment
 * va dans quelle case — d'où le tableau, et le bouton de copie sur la valeur exacte.
 *
 * Les trois formes doivent rester alignées sur `server/src/services/domains.service.js` :
 * nom `_exiplan.<domaine>`, valeur TXT `exiplan-verify=<token>`, cible CNAME
 * `<token>.verify.exiplan.app`. Le serveur tente le TXT puis retombe sur le CNAME : un seul
 * des deux suffit réellement, et c'est écrit à l'écran.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BTN_GHOST_SM, TD, TH } from '../ui/theme.js';

/** Bouton de copie d'une valeur. Le libellé accessible nomme le type : deux boutons « Copier »
 *  identiques ne diraient rien au lecteur d'écran. */
function CopyValue({ type, value }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <code className="font-mono text-xs break-all text-ink">{value}</code>
      <button
        type="button"
        className={`${BTN_GHOST_SM} shrink-0`}
        aria-label={t('organization.domain.dnsCopyAria', { type })}
        onClick={copy}
      >
        {copied ? t('organization.domain.dnsCopied') : '⧉'}
      </button>
    </div>
  );
}

/**
 * Colonne réduite à la largeur de son contenu, pour que seule « Valeur » s'étire.
 *
 * Ce `w-px` n'est **pas** un séparateur : en table layout `auto`, la largeur réellement
 * calculée vient du contenu non sécable, et `w-px` ne sert que de minimum. Le passer à 2px
 * pour satisfaire la règle laisserait une valeur arbitraire que personne ne saurait relire.
 */
// exiui:allow separateur-1px — largeur de colonne, pas un trait
const COLONNE_ETROITE = 'w-px whitespace-nowrap';

/**
 * @param {object} props
 * @param {string} props.domain Domaine revendiqué, tel que déclaré par l'organisation.
 * @param {string} props.token Jeton de vérification émis par le serveur.
 */
export function DnsRecords({ domain, token }) {
  const { t } = useTranslation();
  const fullName = `_exiplan.${domain}`;

  const records = [
    { type: 'TXT', value: t('organization.domain.dnsTxtValue', { token }) },
    { type: 'CNAME', value: t('organization.domain.dnsCnameValue', { token }) },
  ];

  return (
    <div className="max-w-3xl border-2 border-line p-3">
      <p className="text-sm text-ink-soft">{t('organization.domain.dnsInstructions')}</p>
      <p className="mt-2 text-sm font-semibold text-ink">{t('organization.domain.dnsEitherOne')}</p>

      <table className="mt-3 w-full border-collapse">
        <thead>
          <tr>
            <th className={`${TH} ${COLONNE_ETROITE}`}>{t('organization.domain.dnsColumnType')}</th>
            <th className={`${TH} ${COLONNE_ETROITE}`}>{t('organization.domain.dnsColumnHost')}</th>
            <th className={TH}>{t('organization.domain.dnsColumnValue')}</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr key={record.type}>
              <td className={`${TD} ${COLONNE_ETROITE}`}>
                <code className="font-mono text-xs text-ink">{record.type}</code>
              </td>
              <td className={`${TD} ${COLONNE_ETROITE}`}>
                <code className="font-mono text-xs text-ink">
                  {t('organization.domain.dnsHost')}
                </code>
              </td>
              <td className={TD}>
                <CopyValue type={record.type} value={record.value} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="mt-2 text-xs text-ink-faint">
        {t('organization.domain.dnsHostHint', { name: fullName })}
      </p>
      <p className="mt-3 text-xs text-ink-faint">{t('organization.domain.dnsPropagation')}</p>
    </div>
  );
}
